import { SSMLTagsColors } from "shared/constants";
import { extractAttributes, validateSSML } from "./ssmlValidator";

const QUOTE_CHARS = `'"\u2018\u2019\u201C\u201D`;
const QUOTE_PATTERN = `[${QUOTE_CHARS}]`;
const NOT_QUOTE_PATTERN = `[^${QUOTE_CHARS}]`;
const validator = validateSSML;

function generateNumericUID(): string {
  const timestamp = Date.now().toString();
  const random = Math.floor(Math.random() * 1000000)
    .toString()
    .padStart(6, "0");
  return timestamp + random;
}

function getBreakStrengthValue(strength: string): string {
  const strengthMap: Record<string, string> = {
    none: "0ms",
    "x-weak": "250ms",
    weak: "500ms",
    medium: "1000ms",
    strong: "1500ms",
    "x-strong": "2000ms",
  };
  return strengthMap[strength.toLowerCase()] || strength;
}

function titleCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function convertRawSSMLToTextFormat(
  content: string,
  makeNonEditable = false
): {
  converted: string;
  hasInvalidTag: boolean;
} {
  let converted = content;
  let hasInvalidTag = false;

  const invalidate = () => {
    hasInvalidTag = true;
  };

  // NOTE: nonEditableAttr is intentionally NOT applied to voice/style label spans.
  // The cursor is placeable inside them, but typing is blocked
  const nonEditableAttr = makeNonEditable
    ? ' class="mceNonEditable" contenteditable="false"'
    : "";

  // Handle voice tags
  let voiceChanged = true;
  while (voiceChanged) {
    voiceChanged = false;
    converted = converted.replace(
      /<voice\s+([^>]*)>((?:(?!<voice).)*?)<\/voice>/gis,
      (match, attributes, innerContent) => {
        voiceChanged = true;
        const nameMatch = attributes.match(
          new RegExp(
            `name=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const langMatch = attributes.match(
          new RegExp(
            `(?:language|lang)=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );

        if (!nameMatch) {
          invalidate();
          return innerContent;
        }

        /* Skipping the voice tag check as it is being checked by the validator while switching views */
        // const testTag = `<voice ${attributes}></voice>`;
        // const validationResult = validator(testTag);
        // if (!validationResult.valid) {
        //   invalidate();
        //   return innerContent;
        // }

        const uid = generateNumericUID();
        const tagValue = langMatch
          ? `${nameMatch[1]}, ${langMatch[1]}`
          : nameMatch[1];
        const voiceName = titleCase(nameMatch[1]);
        const color = SSMLTagsColors.VoiceTag;

        return `<span data-ssml='voice-${uid}=${tagValue}' style="display:inline-block;"><span only-styling style="color:${color};">[${voiceName}: </span>${innerContent}<span only-styling style="color:${color};">]</span></span>`;
      }
    );
  }

  // Handle style tags
  let styleChanged = true;
  while (styleChanged) {
    styleChanged = false;
    converted = converted.replace(
      /<style\s+([^>]*)>((?:(?!<style).)*?)<\/style>/gis,
      (match, attributes, innerContent) => {
        styleChanged = true;
        const nameMatch = attributes.match(
          new RegExp(
            `name=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const intensityMatch = attributes.match(
          new RegExp(
            `intensity=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        if (!nameMatch || !intensityMatch) {
          invalidate();
          return innerContent;
        }
        const uid = generateNumericUID();
        const tagValue = nameMatch[1];
        const styleName = titleCase(nameMatch[1]);
        const intensity = intensityMatch[1];
        const color = SSMLTagsColors.StyleTag;
        return `<span data-ssml='style-${uid}=${tagValue}, ${intensity}' style="display:inline-block;"><span only-styling style="color:${color};">[${styleName}: </span>${innerContent}<span only-styling style="color:${color};">]</span></span>`;
      }
    );
  }

  // Handle prosody tags
  let prosodyChanged = true;

  while (prosodyChanged) {
    prosodyChanged = false;
    converted = converted.replace(
      /<prosody\s+([^>]*)>((?:(?!<prosody).)*?)<\/prosody>/gis,
      (match, attributes, innerContent) => {
        prosodyChanged = true;
        const testTag = `<prosody ${attributes}></prosody>`;
        const validationResult = validator(testTag);

        if (!validationResult.valid) {
          invalidate();
          return innerContent;
        }
        const allAttrs = extractAttributes(attributes);
        const allowedAttrs = new Set([
          "rate",
          "pitch",
          "volume",
          "duration",
          "contour",
        ]);

        // Check if any attribute is invalid
        for (const attr of allAttrs) {
          if (!allowedAttrs.has(attr.name)) {
            invalidate(); // Reject tag entirely
            return innerContent;
          }
        }

        const uid = generateNumericUID();
        const attrs = [];
        const rateMatch = attributes.match(
          new RegExp(
            `rate=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const pitchMatch = attributes.match(
          new RegExp(
            `pitch=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const volumeMatch = attributes.match(
          new RegExp(
            `volume=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const durationMatch = attributes.match(
          new RegExp(
            `duration=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const contourMatch = attributes.match(
          new RegExp(
            `contour=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );

        if (rateMatch) attrs.push(`rate-${uid}=${rateMatch[1]}`);
        if (pitchMatch) attrs.push(`pitch-${uid}=${pitchMatch[1]}`);
        if (volumeMatch) attrs.push(`volume-${uid}=${volumeMatch[1]}`);
        if (durationMatch) attrs.push(`duration-${uid}=${durationMatch[1]}`);
        if (contourMatch) attrs.push(`contour-${uid}=${contourMatch[1]}`);

        if (attrs.length === 0) {
          invalidate();
          return innerContent;
        }

        const color = SSMLTagsColors.ProsodyTag;
        const dataSSML = attrs.join(",");
        return `<span style="text-decoration: underline; text-decoration-color:${color};" data-ssml='${dataSSML}'>${innerContent}</span>`;
      }
    );
  }

  // Handle break tags (self-closing)
  converted = converted.replace(
    /<break\s+([^>]*)\/>/gis,
    (match, attributes) => {
      const timeMatch = attributes.match(
        new RegExp(
          `time=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
          "i"
        )
      );
      const strengthMatch = attributes.match(
        new RegExp(
          `strength=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
          "i"
        )
      );
      let tagValue: string | null = null;

      if (timeMatch) {
        tagValue = timeMatch[1];
      } else if (strengthMatch) {
        // Convert strength name to time value for display
        tagValue = getBreakStrengthValue(strengthMatch[1]);
      }

      if (!tagValue) {
        invalidate();
        return "";
      }

      const uid = generateNumericUID();
      const color = SSMLTagsColors.BreakTag;
      return `<span class="ssml-break mceNonEditable" data-ssml="break-${uid}=${tagValue}" contenteditable="false" style="color:${color};cursor:default;background-color:transparent;border:0;">[${tagValue}]</span>`;
    }
  );

  // Handle emphasis tags
  let emphasisChanged = true;
  while (emphasisChanged) {
    emphasisChanged = false;
    converted = converted.replace(
      /<emphasis\s+([^>]*)>((?:(?!<emphasis).)*?)<\/emphasis>/gis,
      (match, attributes, innerContent) => {
        emphasisChanged = true;
        const testTag = `<emphasis ${attributes}></emphasis>`;
        const validationResult = validator(testTag);
        const levelMatch = attributes.match(
          new RegExp(
            `level=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );

        if (!levelMatch || !validationResult.valid) {
          invalidate();
          return innerContent;
        }

        const uid = generateNumericUID();
        const color = SSMLTagsColors.EmphasisTag;
        return `<span style="font-weight:600;font-size:16px;color:${color};" data-ssml='emphasis-${uid}=${levelMatch[1]}'>${innerContent}</span>`;
      }
    );
  }

  // Handle phoneme tags
  let phonemeChanged = true;
  while (phonemeChanged) {
    phonemeChanged = false;
    converted = converted.replace(
      /<phoneme\s+([^>]*)>((?:(?!<phoneme).)*?)<\/phoneme>/gis,
      (match, attributes, innerContent) => {
        phonemeChanged = true;
        const testTag = `<phoneme ${attributes}></phoneme>`;
        const validationResult = validator(testTag);
        const alphabetMatch = attributes.match(
          new RegExp(
            `alphabet=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const phMatch = attributes.match(
          new RegExp(
            `ph=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );

        if (!alphabetMatch || !phMatch || !validationResult.valid) {
          invalidate();
          return innerContent;
        }

        const uid = generateNumericUID();
        const color = SSMLTagsColors.PronunciationTag;
        return `<span data-ssml='phoneme-${uid}=${alphabetMatch[1]},${phMatch[1]}' style="display:inline-block;"><span only-styling${nonEditableAttr} style="color:${color};">{</span>${innerContent}<span only-styling${nonEditableAttr} style="color:${color};">}</span></span>`;
      }
    );
  }

  // Handle say-as tags
  let sayAsChanged = true;
  while (sayAsChanged) {
    sayAsChanged = false;
    converted = converted.replace(
      /<say-as\s+([^>]*)>((?:(?!<say-as).)*?)<\/say-as>/gis,
      (match, attributes, innerContent) => {
        sayAsChanged = true;
        const interpretAsMatch = attributes.match(
          new RegExp(
            `interpret-as=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const languageMatch =
          attributes.match(
            new RegExp(
              `language=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
              "i"
            )
          ) ||
          attributes.match(
            new RegExp(
              `lang=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
              "i"
            )
          ) ||
          attributes.match(
            new RegExp(
              `xml:lang=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
              "i"
            )
          );
        const formatMatch = attributes.match(
          new RegExp(
            `format=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const detailMatch = attributes.match(
          new RegExp(
            `detail=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );

        if (!interpretAsMatch) {
          invalidate();
          return innerContent;
        }

        const uid = generateNumericUID();
        const color = SSMLTagsColors.SayAsTag;

        // Build tagValue preserving positions
        // interpret-as (position 0)
        const parts = [interpretAsMatch[1]];

        // Language (position 1) - add empty string if no language but format/detail exists
        if (languageMatch) {
          parts.push(languageMatch[1]);
        } else if (formatMatch || detailMatch) {
          parts.push("");
        }

        // Format (position 2)
        if (formatMatch) {
          parts.push(formatMatch[1]);
        } else if (detailMatch) {
          parts.push("");
        }

        if (detailMatch) {
          parts.push(detailMatch[1]);
        }

        const tagValue = parts.join(", ");

        return `<span style="border-bottom:1px solid;display:inline-block;border-bottom-color:${color};padding-bottom:2px;" data-ssml='say-as-${uid}=${tagValue}'>${innerContent}</span>`;
      }
    );
  }

  // Handle lang tags
  let langChanged = true;
  while (langChanged) {
    langChanged = false;
    converted = converted.replace(
      /<lang\s+([^>]*)>((?:(?!<lang).)*?)<\/lang>/gis,
      (match, attributes, innerContent) => {
        langChanged = true;
        const languageMatch = attributes.match(
          new RegExp(
            `language=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const xmlLangMatch = attributes.match(
          new RegExp(
            `xml:lang=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const langMatch = attributes.match(
          new RegExp(
            `(?:^|\\s)lang=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        const accentMatch = attributes.match(
          new RegExp(
            `accent=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );

        const langValue =
          languageMatch?.[1] || xmlLangMatch?.[1] || langMatch?.[1] || null;

        if (!langValue) {
          invalidate();
          return innerContent;
        }

        const uid = generateNumericUID();
        const color = SSMLTagsColors.LanguageTag;

        // Build tagValue: always include langValue; append accent if present
        const tagValue = accentMatch
          ? `${langValue}, ${accentMatch[1]}`
          : langValue;

        return `<span style="border-bottom:1px solid;display:inline-block;border-bottom-color:${color};padding-bottom:2px;" data-ssml='lang-${uid}=${tagValue}'>${innerContent}</span>`;
      }
    );
  }

  // Handle sub tags
  let subChanged = true;
  while (subChanged) {
    subChanged = false;
    converted = converted.replace(
      /<sub\s+([^>]*)>((?:(?!<sub).)*?)<\/sub>/gis,
      (match, attributes, innerContent) => {
        subChanged = true;
        const aliasMatch = attributes.match(
          new RegExp(
            `alias=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        if (!aliasMatch) {
          invalidate();
          return innerContent;
        }

        const uid = generateNumericUID();
        const color = SSMLTagsColors.ProsodyTag;
        return `<span style="border-bottom:1px solid;display:inline-block;border-bottom-color:${color};padding-bottom:2px;" data-ssml='sub-${uid}=${aliasMatch[1]}'>${innerContent}</span>`;
      }
    );
  }

  // Handle w tags
  let wChanged = true;
  while (wChanged) {
    wChanged = false;
    converted = converted.replace(
      /<w\s+([^>]*)>((?:(?!<w).)*?)<\/w>/gis,
      (match, attributes, innerContent) => {
        wChanged = true;
        const roleMatch = attributes.match(
          new RegExp(
            `role=${QUOTE_PATTERN}(${NOT_QUOTE_PATTERN}+)${QUOTE_PATTERN}`,
            "i"
          )
        );
        if (!roleMatch) {
          invalidate();
          return innerContent;
        }

        const uid = generateNumericUID();
        const color = SSMLTagsColors.PronunciationTag;
        return `<span style="border-bottom:1px solid;display:inline-block;border-bottom-color:${color};padding-bottom:2px;" data-ssml='w-${uid}=${roleMatch[1]}'>${innerContent}</span>`;
      }
    );
  }

  return { converted, hasInvalidTag };
}
